<template>
  <div ref="Drawer" class="relative hidden z-[40]" role="dialog">
    <!-- 背景遮罩 (Backdrop) -->
    <div
      ref="Backdrop"
      @click="$store.commit('SetShopCartStatus', false)"
      class="fixed inset-0 transition-opacity duration-300 ease-in-out opacity-0 bg-black/40 backdrop-blur-sm"
    ></div>

    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="absolute inset-0 overflow-hidden">
        <div
          class="fixed inset-y-0 right-0 flex max-w-full pl-10 pointer-events-none"
        >
          <!-- Drawer Panel -->
          <div
            ref="Panel"
            class="flex flex-col w-screen h-full max-w-md transition-transform duration-300 ease-in-out transform translate-x-full bg-white shadow-2xl pointer-events-auto"
          >
            <!-- 1. Header -->
            <div
              class="flex items-start justify-between px-6 py-5 border-b border-gray-100"
            >
              <h2 class="text-lg font-bold text-gray-900" id="slide-over-title">
                購物車
              </h2>
              <button
                @click="$store.commit('SetShopCartStatus', false)"
                class="text-gray-400 transition-colors hover:text-gray-600"
              >
                <span class="sr-only">Close panel</span>
                <svg
                  class="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>

            <!-- 2. Body (Product List) -->
            <div class="flex-1 p-6 overflow-y-auto no-scrollbar">
              <ul
                id="cart-drawer-list"
                v-if="shop_cart.length > 0"
                class="space-y-6"
              >
                <li
                  class="flex py-2 animate-fade-in"
                  v-for="(item, item_index) in shop_cart"
                  :key="`shop_cart_item_${item_index}`"
                >
                  <div
                    class="flex-shrink-0 w-20 h-20 overflow-hidden border border-gray-200 rounded-md"
                  >
                    <img
                      :src="$ImageUrl(item.image)"
                      class="object-cover object-center w-full h-full"
                    />
                  </div>

                  <div class="flex flex-col flex-1 ml-4">
                    <div>
                      <div
                        class="flex justify-between mb-1 text-base font-medium text-gray-900"
                      >
                        <h3 class="text-sm">
                          <router-link :to="`/product/${item.goodsID}`">
                            {{ item.title }}
                          </router-link>
                        </h3>
                        <p class="flex-shrink-0 ml-4">
                          NT$
                          {{
                            is_member
                              ? $MoneyFormat(
                                  item.stock.MemberSellPrice * item.amount
                                )
                              : $MoneyFormat(item.stock.SellPrice * item.amount)
                          }}
                        </p>
                      </div>
                      <p
                        class="mb-1 text-xs text-gray-500"
                        v-if="item.stock.ColorTitle != '無'"
                      >
                        {{ item.option_name_1 }}: {{ item.stock.ColorTitle }}
                      </p>
                      <p
                        class="mb-1 text-xs text-gray-500"
                        v-if="item.stock.SizeTitle != '無'"
                      >
                        {{ item.option_name_2 }}: {{ item.stock.SizeTitle }}
                      </p>
                    </div>
                    <div class="flex items-end justify-between flex-1 text-sm">
                      <!-- 簡易數量控制器 -->
                      <div
                        class="flex items-center border border-gray-200 rounded"
                      >
                        <button
                          @click="Remove(item_index, 1)"
                          class="px-2 py-1 text-gray-500 hover:bg-gray-50"
                        >
                          -
                        </button>
                        <span class="px-2 text-xs font-medium">{{
                          item.amount
                        }}</span>
                        <button
                          @click="Add(item_index)"
                          class="px-2 py-1 text-gray-500 hover:bg-gray-50"
                        >
                          +
                        </button>
                      </div>

                      <button
                        type="button"
                        @click="Remove(item_index, item.amount)"
                        class="text-xs font-medium text-rose-500 hover:text-rose-700"
                      >
                        移除
                      </button>
                    </div>
                  </div>
                </li>
              </ul>

              <!-- Empty State (Hidden by default) -->
              <div
                id="cart-empty-state"
                v-else
                class="flex flex-col items-center justify-center h-full pt-10 space-y-4 text-center"
              >
                <div
                  class="flex items-center justify-center w-16 h-16 rounded-full bg-gray-50"
                >
                  <svg
                    class="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    ></path>
                  </svg>
                </div>
                <div>
                  <p class="font-medium text-gray-900">購物車是空的</p>
                  <p class="mt-1 text-sm text-gray-500">
                    快去逛逛喜歡的商品吧！
                  </p>
                </div>
                <router-link
                  to="/collections"
                  @click.native="$store.commit('SetShopCartStatus', false)"
                  class="px-6 py-2 mt-4 text-sm font-medium text-white transition-colors bg-black rounded-lg hover:bg-gray-800"
                >
                  前往購物
                </router-link>
              </div>
            </div>

            <!-- 3. Footer (Subtotal & Actions) -->
            <div
              id="cart-footer"
              class="px-6 py-6 border-t border-gray-100 bg-gray-50"
            >
              <div
                class="flex justify-between mb-4 text-base font-medium text-gray-900"
              >
                <p>小計</p>
                <p id="cart-subtotal">NT$ {{ $MoneyFormat(total_price) }}</p>
              </div>
              <p class="mt-0.5 text-xs text-gray-500 mb-6">
                運費與優惠代碼將於結帳步驟計算。
              </p>
              <div class="flex flex-col gap-3">
                <button
                  @click="
                    $router.push('/checkout'),
                      $store.commit('SetShopCartStatus', false)
                  "
                  class="flex items-center justify-center px-6 py-3 text-base font-medium text-white transition-colors border border-transparent rounded-lg shadow-sm bg-primary hover:bg-red-800"
                >
                  前往結帳
                </button>
                <button
                  @click="$store.commit('SetShopCartStatus', false)"
                  class="flex items-center justify-center px-6 py-3 text-base font-medium text-gray-700 transition-colors bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50"
                >
                  繼續購物
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLocalStorage } from "@/common/cookie";
export default {
  name: "ShopCartDrawer",
  methods: {
    GetActiveOption(shopcart_item) {
      const stock = shopcart_item.product_data.Stock.filter((item) => {
        return (
          item.ColorID == shopcart_item.active_option[0] &&
          item.SizeID == shopcart_item.active_option[1]
        );
      });
      return stock.length > 0 ? stock[0] : "error";
    },
    Add(index) {
      // GTM_AddShopCart(this.shopcart[index], 1);
      const currentProduct =
        this.$store.state.shopcart_module.product_data.find(
          (item) => item.GoodsID == this.shop_cart[index].goodsID
        );
      const shop_cart_item = {
        product: currentProduct,
        options: this.shop_cart[index].active_option,
        amount: 1,
      };
      if (getLocalStorage("account_token")) {
        this.$store.dispatch("shopcart_module/AddShopCart", shop_cart_item);
      } else {
        this.$store.commit("shopcart_module/AddShopCart", shop_cart_item);
      }
    },
    Remove(index, count, type = "normal_product") {
      if (type == "normal_product") {
        // GTM_RemoveShopCart(this.shopcart[index], count);
      }
      if (getLocalStorage("account_token")) {
        this.$store.dispatch("shopcart_module/RemoveShopCart", {
          index: index,
          amount: count,
        });
      } else {
        this.$store.commit("shopcart_module/RemoveShopCart", {
          index: index,
          amount: count,
        });
      }
    },
    OpenDrawer() {
      const drawer = this.$refs.Drawer;
      const backdrop = this.$refs.Backdrop;
      const panel = this.$refs.Panel;

      drawer.classList.remove("hidden");

      setTimeout(() => {
        backdrop.classList.remove("opacity-0");
        panel.classList.remove("translate-x-full");
      }, 10);
    },
    CloseDrawer() {
      const drawer = this.$refs.Drawer;
      const backdrop = this.$refs.Backdrop;
      const panel = this.$refs.Panel;

      backdrop.classList.add("opacity-0");
      panel.classList.add("translate-x-full");

      setTimeout(() => {
        drawer.classList.add("hidden");
      }, 300);
    },
  },
  computed: {
    // 是否登入會員
    is_member() {
      return getLocalStorage("account_token");
    },
    shop_cart_open() {
      return this.$store.state.shop_cart_open;
    },
    shop_cart() {
      return this.$store.state.shopcart_module.shopcart;
    },
    shopcart_count() {
      let count = 0;
      this.$store.state.shopcart_module.shopcart.forEach((item) => {
        count += item.amount;
      });
      return count;
    },
    total_price() {
      let total = 0;
      if (this.is_member) {
        this.$store.state.shopcart_module.shopcart.forEach((item) => {
          total += item.amount * item.stock.MemberSellPrice;
        });
      } else {
        this.$store.state.shopcart_module.shopcart.forEach((item) => {
          total += item.amount * item.stock.SellPrice;
        });
      }
      return total;
    },
  },
  watch: {
    shop_cart_open() {
      if (this.shop_cart_open) {
        this.OpenDrawer();
      } else {
        this.CloseDrawer();
      }
    },
  },
};
</script>
